import { Portfolio } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { getCart } from 'api/operations/cartServiceV2';
import { getConfigByDate } from 'api/operations/domainServiceV2';
import { getGroupById } from 'api/operations/groupService';
import { getPortfoliosById } from 'api/operations/productServiceV2';
import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { Cart } from 'types/cartServiceV2';
import { ConfigProps } from 'types/domainServiceV2';
import { EnrollmentPeriod, Proposal } from 'types/enrollmentService';
import { Group } from 'types/groupService';
import EnrollmentPeriodModule from '../enrollment/enrollmentPeriodModule';

export interface ProposalDomainParams {
  cartId: string;
  groupId: string;
  enrollmentPeriodId: string;
}

export interface ProposalDomainData {
  cart: Cart;
  group: Group;
  enrollmentPeriod: EnrollmentPeriod;
  portfolios: Portfolio[];
}

export default class ProposalDomainModule {
  private rootStore: RootStore;
  private _cart: Cart | null = null;
  private _group: Group | null = null;
  private _enrollmentModule: EnrollmentPeriodModule | null = new EnrollmentPeriodModule();
  private _portfolios: Portfolio[] = [];
  private _domainConfigProps: ConfigProps | null = null;

  private async loadCart(cartId: string): Promise<void> {
    this._cart = await getCart(cartId);
  }

  private async loadGroup(groupId: string): Promise<void> {
    this._group = await getGroupById(groupId);
  }

  private async loadPortfoliosByCart(): Promise<void> {
    if (!this._group) {
      throw new Error(`No group uploaded!`);
    }
    if (!this._cart) {
      throw new Error(`No cart uploaded!`);
    }

    const portfolios = await getPortfoliosById(
      this._cart.cartItems.map((item) => toJS(item)).slice(),
      undefined,
      this._group.id,
      this._cart.id,
      'no-cache',
    );

    runInAction(() => {
      const sortedPortfolios = portfolios.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
      this._portfolios = sortedPortfolios;
    });
  }

  private async loadDomainConfig(): Promise<void> {
    if (!this._cart) {
      throw new Error(`No cart uploaded!`);
    }
    const effectiveDate = this._cart.lastUpdateBrowsingCriteria.effectiveDate;
    const rateSegment = this._cart.rateSegment;

    const configs = await getConfigByDate(this.sponsorId, effectiveDate);
    this._domainConfigProps = configs[rateSegment];
  }

  public async loadProposalDomainData({ cartId, groupId, enrollmentPeriodId }: ProposalDomainParams): Promise<void> {
    await Promise.all([this.loadCart(cartId), this.loadGroup(groupId)]);
    await runInAction(async () => {
      await Promise.all([
        this._enrollmentModule?.uploadEnrollmentPeriod(enrollmentPeriodId),
        this.loadPortfoliosByCart(),
        this.loadDomainConfig(),
      ]);
    });
  }

  public get sponsorId(): string {
    return this.rootStore.userStore.sponsorId;
  }

  public get proposalDomainData(): ProposalDomainData | null {
    if (!this._cart || !this._group || !this._enrollmentModule?.enrollmentPeriod || !this._portfolios) {
      return null;
    }
    return {
      cart: this._cart,
      group: this._group,
      enrollmentPeriod: this._enrollmentModule?.enrollmentPeriod,
      portfolios: this._portfolios,
    };
  }

  public get confirmedProposal(): Proposal | null {
    return this._enrollmentModule?.enrollmentProposalData?.confirmedProposal || null;
  }

  public get domainConfigProps(): ConfigProps | null {
    return this._domainConfigProps;
  }

  constructor() {
    this.rootStore = getRootStoreInstance();
    makeObservable<
      ProposalDomainModule,
      | '_cart'
      | '_group'
      | '_enrollmentModule'
      | '_portfolios'
      | '_domainConfigProps'
      | 'loadCart'
      | 'loadGroup'
      | 'loadPortfoliosByCart'
      | 'loadDomainConfig'
    >(this, {
      _cart: observable,
      _group: observable,
      _enrollmentModule: observable,
      _portfolios: observable,
      _domainConfigProps: observable,

      loadCart: action.bound,
      loadGroup: action.bound,
      loadDomainConfig: action.bound,
      loadPortfoliosByCart: action.bound,

      loadProposalDomainData: action.bound,

      sponsorId: computed,
      confirmedProposal: computed,
      proposalDomainData: computed,
      domainConfigProps: computed,
    });
  }
}
